import { ComponentProps, useMemo } from "react"
import { Autocomplete, TextField } from "@mui/material"
import { FieldValues, UseControllerProps, useController } from "react-hook-form-mui"
import { useQuery } from "@tanstack/react-query"
import { apiClient, apiClientHooks } from "../../bootstrapping/InitApiClient"

type CaseWorkerAutocompleteProps<T extends FieldValues> =
  Omit<
    ComponentProps<typeof Autocomplete>,
    'options' | 'renderInput' | 'freeSolo' | 'value' | 'onChange' | 'isOptionEqualToValue' | 'onInputChange' |
    'renderOption' | 'renderTags'
  > &
  UseControllerProps<T> &
  {
    label: ComponentProps<typeof TextField>['label'],
    customerId: string,
  }

/**
 * Optional case-worker picker for invite creation: the admins of the customer,
 * flagged case workers first.
 */
const CaseWorkerAutocomplete = <T extends FieldValues>(props: CaseWorkerAutocompleteProps<T>) => {
  const {
    // UseControllerProps
    name, rules, control, label, shouldUnregister, defaultValue,
    // Custom
    customerId,
    // AutocompleteProps
    ...autocompleteProps
  } = props;

  const {
    field: { ref, value, onChange },
    formState: { errors: { [name]: error } },
  } = useController<T>({ name, control });

  const adminProfiles = useQuery({
    queryKey: [...apiClientHooks.getKeyByAlias('getProfiles'), 'customer-admins', customerId],
    queryFn: async () => apiClient.getProfiles({
      queries: {
        page_index: '0',
        page_size: '100',
        columnFilters: [{ id: 'admin_customer_ids', value: customerId }],
      },
    }),
    enabled: !!customerId,
    refetchOnWindowFocus: false,
  })

  const options = useMemo(() => {
    const rows = adminProfiles.data?.rows ?? []
    return rows
      .filter(row => row.active !== false)
      .map(row => ({
        id: row.id,
        label: `${row.given_name} ${row.family_name}`.trim() || row.email,
        isCaseWorker: (row.case_worker_customer_ids ?? []).includes(customerId),
      }))
      .sort((a, b) => Number(b.isCaseWorker) - Number(a.isCaseWorker) || a.label.localeCompare(b.label))
  }, [adminProfiles.data, customerId])

  const selectedOption = options.find(option => option.id === value) ?? null

  return (
    <Autocomplete
      {...autocompleteProps}
      ref={ref}
      options={options}
      value={selectedOption}
      getOptionLabel={option => {
        const o = option as typeof options[number] | string
        return typeof o === 'string' ? o : `${o.label}${o.isCaseWorker ? '' : ' (manager)'}`
      }}
      isOptionEqualToValue={(option, value) => {
        const o = option as typeof options[number] | null
        const v = value as typeof options[number] | null
        return o?.id === v?.id
      }}
      onChange={(_, selectedOption) => {
        onChange((selectedOption as typeof options[number] | null)?.id ?? '')
      }}
      renderInput={(params) => <>
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={error && `${error.message}`}
        />
        <input type="hidden" name={name} value={value ?? ''} />
      </>}
    />
  )
}

export default CaseWorkerAutocomplete
